import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { showError } from "@/lib/errors";
import { toast } from "sonner";
import { Sparkles, Link2, Loader2, Send, Image as ImageIcon, AlertTriangle, X, Plus } from "lucide-react";
import { BookmarkletPromoCard } from "@/components/BookmarkletPromoCard";

const CATEGORIES = [
  ["actualite", "Actualité sportive"],
  ["resultats", "Résultats sportifs"],
  ["competition_a_venir", "Compétition à venir"],
  ["retour_competition", "Retour de compétition"],
  ["portrait", "Portrait"],
  ["interview", "Interview"],
  ["billet_humeur", "Billet d'humeur"],
  ["soiree", "Commentaire de soirée"],
  ["communique_club", "Communiqué club"],
  ["communique_federation", "Communiqué fédération"],
  ["galerie", "Galerie photos"],
  ["video", "Vidéo"],
  ["autre", "Autre"],
];

const RIGHTS_ITEMS = [
  ["author_rights", "Je respecte les droits d'auteur des sources citées."],
  ["no_private", "Je ne publie pas de contenu privé sans autorisation explicite."],
  ["personal_rights", "Je dispose des autorisations nécessaires pour les personnes identifiables sur les photos."],
  ["admin_override", "Je comprends qu'un administrateur peut modifier, refuser ou retirer mon contenu à tout moment."],
];

function AssociationPicker({ associations, onChange }) {
  const [kind, setKind] = useState("athlete");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const collections = { athlete: "athletes", coach: "coaches", club: "clubs", federation: "federations" };

  useEffect(() => {
    if (!q || q.length < 2) { setResults([]); return; }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/${collections[kind]}?q=${encodeURIComponent(q)}&limit=8`);
        if (!cancelled) setResults(data?.items || []);
      } catch { /* ignore */ } finally { if (!cancelled) setLoading(false); }
    }, 220);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, kind]);

  const add = (it) => {
    if (associations.some((a) => a.entity_type === kind && a.entity_id === it.id)) {
      toast.info("Déjà associé.");
      return;
    }
    onChange([...associations, { entity_type: kind, entity_id: it.id, entity_name: it.full_name || it.name, placements: ["articles"], notify_owner: true }]);
    setQ("");
  };
  const remove = (idx) => onChange(associations.filter((_, i) => i !== idx));

  return (
    <div className="space-y-2.5" data-testid="assoc-picker">
      <div className="flex flex-wrap gap-1.5">
        {Object.keys(collections).map((k) => (
          <button key={k} type="button" onClick={() => setKind(k)} data-testid={`assoc-kind-${k}`}
            className={`text-xs px-2.5 py-1 rounded-md border ${kind === k ? "bg-[hsl(var(--aito-lagoon))] text-white border-transparent" : "hover:bg-accent"}`}>
            {k === "athlete" ? "Athlète" : k === "coach" ? "Coach" : k === "club" ? "Club" : "Fédération"}
          </button>
        ))}
      </div>
      <div className="relative">
        <Input data-testid="assoc-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Rechercher ${kind}…`} />
        {q.length >= 2 && (
          <div className="absolute z-20 mt-1 w-full rounded-md border bg-popover shadow-md max-h-60 overflow-auto">
            {loading && <div className="px-3 py-2 text-xs text-muted-foreground">Recherche…</div>}
            {!loading && results.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">Aucun résultat.</div>}
            {results.map((it) => (
              <button key={it.id} type="button" onClick={() => add(it)} data-testid={`assoc-pick-${it.id}`}
                className="w-full text-left px-3 py-2 hover:bg-accent text-sm">
                {it.full_name || it.name}
              </button>
            ))}
          </div>
        )}
      </div>
      {associations.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="assoc-list">
          {associations.map((a, idx) => (
            <Badge key={idx} variant="secondary" className="gap-1.5 pr-1">
              <span className="capitalize text-[10px]">{a.entity_type}</span> · {a.entity_name}
              <button type="button" onClick={() => remove(idx)} className="rounded-full h-4 w-4 inline-flex items-center justify-center hover:bg-foreground/10" aria-label="Retirer">
                <X className="h-2.5 w-2.5" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ContributeCuratorPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [sourceName, setSourceName] = useState("");
  const [category, setCategory] = useState("actualite");
  const [discipline, setDiscipline] = useState("");
  const [associations, setAssociations] = useState([]);
  const [rightsConfirmed, setRightsConfirmed] = useState(false);
  const [acks, setAcks] = useState({});
  const [extracting, setExtracting] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) navigate("/connexion?next=/contribuer");
  }, [authLoading, user, navigate]);

  const canSubmit = useMemo(() => {
    if (!title.trim()) return false;
    if (!rightsConfirmed) return false;
    const all = RIGHTS_ITEMS.every(([k]) => acks[k]);
    return all;
  }, [title, rightsConfirmed, acks]);

  const doExtract = async () => {
    if (!url.trim()) { toast.error("Colle d'abord une URL."); return; }
    setExtracting(true);
    try {
      const { data } = await api.post("/curator/extract", { url: url.trim() });
      setTitle(data.title || "");
      setSummary(data.summary || data.description || "");
      setImageUrl(data.image_url || "");
      setSourceName(data.source_name || "");
      setCategory(data.category || "actualite");
      toast.success("Métadonnées récupérées. Vérifie et enrichis avant de soumettre.");
    } catch (e) {
      showError(e, { fallback: "Échec de l'extraction" });
    } finally { setExtracting(false); }
  };

  const submit = async () => {
    if (!canSubmit) { toast.error("Confirme tous les engagements de droits."); return; }
    setSubmitting(true);
    try {
      const acknowledgements = RIGHTS_ITEMS.filter(([k]) => acks[k]).map(([k]) => k);
      const payload = {
        url: url.trim() || null,
        source_name: sourceName.trim() || null,
        title: title.trim(),
        summary: summary.trim() || null,
        image_url: imageUrl.trim() || null,
        category, discipline: discipline.trim() || null,
        rights_confirmed: true,
        rights_acknowledgements: acknowledgements,
        associations,
      };
      const { data } = await api.post("/curator/items", payload);
      toast.success("Contribution soumise — l'équipe éditoriale la validera bientôt.");
      navigate(`/dashboard`);
    } catch (e) {
      showError(e, { fallback: "Échec de la soumission" });
    } finally { setSubmitting(false); }
  };

  if (!user) return null;

  return (
    <div className="max-w-4xl mx-auto py-6 px-4 space-y-5" data-testid="contribute-page">
      <div>
        <h1 className="text-2xl font-display font-bold flex items-center gap-2">
          <Sparkles className="h-6 w-6 text-[hsl(var(--aito-lagoon))]" />
          Proposer un contenu éditorial
        </h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          Colle un lien d'article, de post, de résultat ou de vidéo — ou rédige directement une carte. Aucun contenu n'est publié sans validation par la rédaction.
        </p>
      </div>

      <BookmarkletPromoCard />

      <Card className="p-5 space-y-3">
        <h2 className="font-display font-semibold text-sm flex items-center gap-1.5"><Link2 className="h-4 w-4" />Source (optionnel)</h2>
        <div className="flex items-center gap-2">
          <Input data-testid="contribute-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://… (article, vidéo, post public)" />
          <Button onClick={doExtract} disabled={extracting || !url.trim()} data-testid="contribute-extract">
            {extracting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}
            Récupérer
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground italic">Le système récupère titre, vignette et description, puis génère un résumé court reformulé. L'article complet n'est jamais copié.</p>
      </Card>

      <Card className="p-5 space-y-3">
        <h2 className="font-display font-semibold text-sm">Aperçu de la carte</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Titre *</label>
            <Input data-testid="contribute-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titre éditorial" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Source / média</label>
            <Input data-testid="contribute-source" value={sourceName} onChange={(e) => setSourceName(e.target.value)} placeholder="Tahiti Infos, La Dépêche…" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-medium text-muted-foreground">Vignette (URL image)</label>
            <Input data-testid="contribute-image" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="https://…image.jpg" />
            {imageUrl && <img src={imageUrl} alt="" className="mt-2 h-32 rounded-md object-cover border" onError={(e) => { e.currentTarget.style.opacity = "0.3"; }} />}
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Catégorie</label>
            <select data-testid="contribute-category" value={category} onChange={(e) => setCategory(e.target.value)} className="w-full rounded-md border bg-background px-3 py-2 text-sm">
              {CATEGORIES.map(([k, lbl]) => <option key={k} value={k}>{lbl}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Discipline</label>
            <Input data-testid="contribute-discipline" value={discipline} onChange={(e) => setDiscipline(e.target.value)} placeholder="Ex. discipline (surf, va'a, athlétisme, boxe…)" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-medium text-muted-foreground">Résumé court (2-3 phrases)</label>
            <textarea data-testid="contribute-summary" value={summary} onChange={(e) => setSummary(e.target.value)} rows={3} className="w-full rounded-md border bg-background px-3 py-2 text-sm" placeholder="Résumé sobre et factuel, pas de copie de l'article complet." />
            <p className="text-[11px] text-muted-foreground mt-1">{summary.length} caractères. Idéal : 100 à 300.</p>
          </div>
        </div>
      </Card>

      <Card className="p-5 space-y-3">
        <h2 className="font-display font-semibold text-sm">Associer à des profils (optionnel)</h2>
        <p className="text-[11px] text-muted-foreground">Si le contenu mentionne un athlète, un coach, un club ou une fédération, sélectionne-les. Les propriétaires recevront une proposition d'ajout dans leur fiche.</p>
        <AssociationPicker associations={associations} onChange={setAssociations} />
      </Card>

      <Card className="p-5 space-y-3 border-2 border-amber-500/40 bg-amber-500/5">
        <h2 className="font-display font-semibold text-sm flex items-center gap-1.5 text-amber-700 dark:text-amber-400">
          <AlertTriangle className="h-4 w-4" />Engagement obligatoire (RGPD & droits)
        </h2>
        <label className="flex items-start gap-2 text-sm cursor-pointer">
          <Checkbox checked={rightsConfirmed} onCheckedChange={(v) => setRightsConfirmed(!!v)} data-testid="contribute-rights-main" className="mt-0.5" />
          <span className="font-medium">Je confirme disposer des droits nécessaires pour proposer ce contenu, ces images ou ces informations, et je comprends qu'ils seront soumis à validation avant publication.</span>
        </label>
        <div className="pl-6 space-y-1.5">
          {RIGHTS_ITEMS.map(([k, lbl]) => (
            <label key={k} className="flex items-start gap-2 text-xs cursor-pointer">
              <Checkbox checked={!!acks[k]} onCheckedChange={(v) => setAcks((p) => ({ ...p, [k]: !!v }))} data-testid={`contribute-rights-${k}`} className="mt-0.5" />
              <span>{lbl}</span>
            </label>
          ))}
        </div>
      </Card>

      <div className="flex items-center justify-end gap-2">
        <Link to="/dashboard"><Button variant="ghost">Annuler</Button></Link>
        <Button onClick={submit} disabled={!canSubmit || submitting} data-testid="contribute-submit" className="bg-[hsl(var(--aito-lagoon))] hover:bg-[hsl(var(--aito-lagoon-2))] text-black">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />}
          Soumettre à validation
        </Button>
      </div>
    </div>
  );
}
